import { useEffect, useState } from "react";

import useEmblaCarousel from "embla-carousel-react";
import Box from "@mui/material/Box";
import { SxProps, Theme } from "@mui/material/styles";
import { ArrowBackOutlined, ArrowForwardOutlined } from "@mui/icons-material";

import { ContentTile } from '@/types/generated';
import { IImage } from "@/components/ImageAsset/ImageAsset";

import ExploreCard from "../ExploreCard/ExploreCard";
import CustomTypography from "../UI/Typography/CustomTypography";
import CustomIconButton from "../UI/Button/CustomIconButton";

import { exploreCarouselStyles } from "./ExploreCarousel.styles";

interface ExploreCarouselProps {
    tiles: Array<ContentTile | null>;
    onTileClick?: (tile: ContentTile, href: string) => void;
}

/**
 * Extracts the href from a ContentTile
 */
const getTileHref = (tile: ContentTile): string => {
    return tile.internalPage?.path || tile.externalUrl || "#";
};

const getImageUrl = (img?: IImage) => img?.image?.cdn_url ?? img?.image?.url;

/**
 * Extracts the image URL from a ContentTile
 */
const getTileImageUrl = (tile: ContentTile): string => {
    return getImageUrl(tile.image as IImage) ?? '';
};

const ExploreCarousel = ({ tiles, onTileClick }: ExploreCarouselProps) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        dragFree: true,
        loop: false,
        containScroll: "trimSnaps",
    });

    const [selectedIndex, setSelectedIndex] = useState(0);

    // Filter out null tiles
    const validTiles = tiles.filter(
        (tile): tile is ContentTile => tile !== null,
    );
    const slideCount = validTiles.length;

    useEffect(() => {
        if (!emblaApi) return;

        const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
        emblaApi.on("select", onSelect);
        onSelect();

        return () => {
            emblaApi.off("select", onSelect);
        };
    }, [emblaApi]);

    // Don't render if no valid tiles
    if (slideCount === 0) {
        return null;
    }

    const handlePrevClick = () => {
        emblaApi?.scrollPrev();
    };

    const handleNextClick = () => {
        emblaApi?.scrollNext();
    };

    const handleDotClick = (index: number) => {
        emblaApi?.scrollTo(index);
    };

    const handleCardClick = (tile: ContentTile) => {
        const href = getTileHref(tile);
        onTileClick?.(tile, href);
    };

    return (
        <Box sx={exploreCarouselStyles.container}>
            {/* Header */}
            <CustomTypography sx={exploreCarouselStyles.header}>
                Explore
            </CustomTypography>

            {/* Carousel viewport */}
            <Box ref={emblaRef} sx={{ overflow: 'hidden' }}>
                <Box sx={exploreCarouselStyles.slidesContainer}>
                    {validTiles.map((tile) => (
                        <ExploreCard
                            key={tile.internalPage?.sys?.id || tile.externalUrl || tile.title}
                            title={tile.title || ""}
                            description={tile.description}
                            imageUrl={getTileImageUrl(tile)}
                            href={getTileHref(tile)}
                            onClick={() => handleCardClick(tile)}
                        />
                    ))}
                </Box>
            </Box>

            {/* Navigation controls */}
            <Box sx={exploreCarouselStyles.navigation}>
                {/* Previous arrow */}
                <CustomIconButton
                    onClick={handlePrevClick}
                    disabled={selectedIndex === 0}
                    sx={selectedIndex === 0 ? exploreCarouselStyles.arrowDisabled : exploreCarouselStyles.arrow}
                    aria-label="Previous slide"
                >
                    <ArrowBackOutlined style={{ fontSize: 28 }} />
                </CustomIconButton>

                {/* Dot indicators */}
                <Box sx={exploreCarouselStyles.dots}>
                    {validTiles.map((tile, index) => (
                        <Box
                            key={tile.internalPage?.sys?.id || tile.externalUrl || `dot-${index}`}
                            component="button"
                            type="button"
                            onClick={() => handleDotClick(index)}
                            sx={
                                (selectedIndex === index
                                    ? { ...exploreCarouselStyles.dot, ...exploreCarouselStyles.dotActive }
                                    : exploreCarouselStyles.dot) as SxProps<Theme>
                            }
                            aria-label={`Go to slide ${index + 1}`}
                            aria-current={selectedIndex === index ? "true" : undefined}
                        />
                    ))}
                </Box>

                {/* Next arrow */}
                <CustomIconButton
                    onClick={handleNextClick}
                    disabled={selectedIndex === slideCount - 1}
                    sx={selectedIndex === slideCount - 1 ? exploreCarouselStyles.arrowDisabled : exploreCarouselStyles.arrow}
                    aria-label="Next slide"
                >
                    <ArrowForwardOutlined style={{ fontSize: 28 }} />
                </CustomIconButton>
            </Box>
        </Box>
    );
};

export default ExploreCarousel;